"use client";

import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { toWordGroups } from "./text-reveal";

const EASE = [0.16, 1, 0.3, 1] as const;

export interface TextRevealRotateProps {
  texts: string[];
  intervalMs?: number;
  className?: string;
  stagger?: number;
  blur?: number;
  yOffset?: string;
}

export function TextRevealRotate({
  texts,
  intervalMs = 3000,
  className,
  stagger = 0.05,
  blur = 5,
  yOffset = "60%",
}: TextRevealRotateProps) {
  const [index, setIndex] = useState(0);
  const reduce = useReducedMotion();

  useEffect(() => {
    if (texts.length < 2) return;
    const id = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % texts.length);
    }, intervalMs);
    return () => window.clearInterval(id);
  }, [texts.length, intervalMs]);

  const current = texts.length > 0 ? texts[index % texts.length] : "";

  // One motion parent owns both directions. Handing the exit to descendants of
  // a plain element instead lets AnimatePresence unmount the subtree before
  // their stagger has run, which reads as the words being cut off.
  const container = {
    enter: {},
    show: { transition: { staggerChildren: stagger } },
    exit: { transition: { staggerChildren: stagger } },
  };

  const word = reduce
    ? {
        enter: { opacity: 0 },
        show: { opacity: 1, transition: { duration: 0.25, ease: EASE } },
        exit: { opacity: 0, transition: { duration: 0.2, ease: EASE } },
      }
    : {
        enter: { y: yOffset, opacity: 0, filter: `blur(${blur}px)` },
        show: {
          y: 0,
          opacity: 1,
          filter: "blur(0px)",
          transition: { duration: 0.5, ease: EASE },
        },
        exit: {
          y: `-${yOffset}`,
          opacity: 0,
          filter: `blur(${blur}px)`,
          transition: { duration: 0.34, ease: EASE },
        },
      };

  return (
    <AnimatePresence mode="wait">
      <motion.span
        animate="show"
        // The vertical padding is load-bearing: callers sit inside an
        // `overflow-hidden` row, and a blurred glyph spreads past its own box,
        // so without the extra room the fade ends in a hard cut.
        className={cn("block py-2", className)}
        exit="exit"
        initial="enter"
        key={index}
        variants={container}
      >
        {toWordGroups(current).map((group, i) => (
          <motion.span
            className="inline-block whitespace-pre will-change-transform"
            key={`${group.text}-${i}`}
            variants={word}
          >
            {group.text + group.trailing}
          </motion.span>
        ))}
      </motion.span>
    </AnimatePresence>
  );
}
